import { useInfiniteQuery } from '@tanstack/react-query'
import { useDebounce } from './useDebounce'
import { searchPlans, type PlanSearchResponse } from '@/api/planSearch'
import { queryKeys } from '@/lib/queryKeys'

interface UsePlanSearchParams {
  query: string
  seasonId?: string
  debounceMs?: number
}

/**
 * Hook for searching plans with debounced query and infinite scroll pagination
 * When query is empty, returns latest plans ordered by modified_at
 * @param params - Search parameters
 */
export function usePlanSearch({
  query,
  seasonId,
  debounceMs = 300,
}: UsePlanSearchParams) {
  const debouncedQuery = useDebounce(query, debounceMs)

  return useInfiniteQuery<PlanSearchResponse>({
    queryKey: queryKeys.planSearch.byQuery(debouncedQuery, seasonId),
    queryFn: ({ pageParam = 0 }) =>
      searchPlans({
        q: debouncedQuery || undefined,
        seasonId: seasonId || undefined,
        orderBy: '-modified_at',
        offset: pageParam as number,
        limit: 20,
      }),
    getNextPageParam: lastPage =>
      lastPage.hasMore ? lastPage.nextOffset : undefined,
    initialPageParam: 0,
    staleTime: 1000 * 60, // Cache for 1 minute
  })
}
